"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, ArrowRight, ShieldCheck, PhoneCall } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Richmond?",
  answer: "Yes. We regularly supply and erect scaffolding across Richmond upon Thames and neighbouring South West London and Surrey areas, serving residential homeowners, commercial sites, and trade contractors."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Richmond?",
  answer: "We supply independent tied scaffolding, temporary roof covers, edge protection, chimney stacks, birdcage scaffolding, and bespoke access towers for domestic renovations, commercial buildings, and industrial projects."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We design and install dedicated roof access scaffolding, perimeter edge protection, chimney saddles, and weather-proof temporary roof coverings tailored for roofing contractors and solar panel installers."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We collaborate directly with main contractors, developers, local authorities, and facilities managers. Every structure comes with site-specific RAMS and complies with NASC and CHAS standards."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our team handles narrow side passages, tight residential boundary lines, public highway placements, and cantilevered structures following a thorough site inspection and access design plan."
}, {
  id: "item-6",
  question: "How do I request a quote in Richmond?",
  answer: "You can send your project details, architectural drawings, or site photographs through our online quote request form, or contact our team directly to arrange an on-site survey."
}];
export default function FaqRichmond() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="faqrichmond" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wide uppercase text-primary border-primary/20 bg-primary/5">
            <HelpCircle className="size-3.5 text-primary mr-1" />
            Richmond coverage
          </Badge>

          <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground max-w-2xl">
            Frequently asked questions for Richmond projects
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl">
            Clear answers on coverage, site access, scaffold types, and our quoting process.
          </p>
        </div>

        {/* Accordion Component */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="bg-card text-card-foreground rounded-xl border border-border shadow-sm p-4 sm:p-8">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                <AccordionItem value={item.id} className="border-border">
                  <AccordionTrigger className="text-left font-default text-base sm:text-lg font-semibold text-foreground hover:no-underline hover:text-primary transition-colors py-4 sm:py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4 pt-1">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              </div>)}
          </Accordion>
        </motion.div>

        {/* Accreditation & Support Footer Strip */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3 text-sm text-muted-foreground text-center md:text-left">
            <div className="p-2 rounded-md bg-muted text-foreground shrink-0">
              <ShieldCheck className="size-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">Accredited scaffolding contractor</p>
              <p className="text-xs text-muted-foreground">
                NASC and CHAS approved operations across London and the South East.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 justify-center">
            <Button variant="outline" asChild>
              <Link to="/HealthSafetyAccreditationsPage">
                View safety standards
              </Link>
            </Button>
            <Button asChild>
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}